/**
 * iter700 / iter702 — Customer Display page.
 *
 * Lives at `/customer-display` and is opened from the Cashier POS via
 * a `window.open(...)` (the "Customer Display" toolbar chip in iter699).
 * Drag this window onto the second monitor that's facing the buyer so
 * they see their cart update line-by-line as the cashier rings it up.
 *
 * Wiring (zero backend round-trip during the order):
 *   • The Cashier POS broadcasts cart changes on
 *     `BroadcastChannel('smarto_customer_display')` every time the
 *     cart array changes.
 *   • This page listens on the same channel and re-renders. Falls back
 *     to localStorage polling if BroadcastChannel isn't supported
 *     (older Safari / private windows).
 *   • Branding (company name, logo, currency) loaded once from
 *     `/api/branding/public` so the page can be opened from a
 *     public/anonymous browser without a cashier_token.
 *
 * iter702 — Ad / promo rotator:
 *   • When the cart is empty, instead of a static "Welcome", the page
 *     cycles through full-screen promo slides set by the admin (see
 *     `CustomerDisplayPromosPage`). The slides themselves are fetched
 *     by the authenticated Cashier POS and broadcast over the same
 *     BroadcastChannel — keeping the customer display fully anonymous.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { resolveUploadUrl } from '@/lib/uploadUrl';
import { Info, ShoppingBag, CheckCircle2, Heart, Sparkles, Plus } from 'lucide-react';

const CHANNEL = 'smarto_customer_display';
const STORAGE_KEY = 'cd_state_v1';
const PROMOS_KEY = 'cd_promos_v1';
const UPSELLS_KEY = 'cd_upsells_v1';

// Minimal currency helper — pulls the tenant's currency code from branding.
const currencyOf = (b) => b?.currency_code || b?.currency_symbol || '';

export default function CustomerDisplayPage() {
  const [state, setState] = useState({
    cart: [], subtotal: 0, tax: 0, total: 0, discount: 0,
    branch: '', orderNo: '', status: 'idle', // idle | active | paid
  });
  const [branding, setBranding] = useState(null);
  const [promos, setPromos] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  // Branding once at boot. Public endpoint — no token needed.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/public`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setBranding(data || null);
      } catch { /* offline display is still useful */ }
    })();
    return () => { alive = false; };
  }, []);

  // Live cart + promo updates. Prefer BroadcastChannel; fall back to a
  // 700 ms localStorage poll for browsers that don't support it.
  useEffect(() => {
    let bc = null;
    let pollId = null;
    const applyCart = (incoming) => {
      if (!incoming || typeof incoming !== 'object') return;
      setState((prev) => ({ ...prev, ...incoming }));
    };
    const applyPromos = (incoming) => {
      if (!incoming || !Array.isArray(incoming.promos)) return;
      setPromos(incoming.promos);
      try { localStorage.setItem(PROMOS_KEY, JSON.stringify(incoming.promos)); } catch { /* ignored */ }
    };
    const applyUpsells = (incoming) => {
      if (!incoming || !Array.isArray(incoming.upsells)) return;
      setUpsells(incoming.upsells);
      try { localStorage.setItem(UPSELLS_KEY, JSON.stringify(incoming.upsells)); } catch { /* ignored */ }
    };

    // Seed from any cached state (so re-opening the window mid-order
    // restores the live cart immediately).
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) applyCart(JSON.parse(cached));
      const cachedPromos = localStorage.getItem(PROMOS_KEY);
      if (cachedPromos) setPromos(JSON.parse(cachedPromos));
      const cachedUpsells = localStorage.getItem(UPSELLS_KEY);
      if (cachedUpsells) setUpsells(JSON.parse(cachedUpsells));
    } catch { /* ignored */ }

    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (e) => {
        const msg = e.data;
        if (msg && msg.type === 'promos') applyPromos(msg);
        else if (msg && msg.type === 'upsells') applyUpsells(msg);
        else applyCart(msg);
      };
    } else {
      // Legacy fallback — poll localStorage. Same-tab storage events
      // don't fire on the writer, so we re-read on a slow timer.
      let lastRaw = null;
      let lastPromos = null;
      pollId = setInterval(() => {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw && raw !== lastRaw) {
          lastRaw = raw;
          try { applyCart(JSON.parse(raw)); } catch { /* ignored */ }
        }
        const pRaw = localStorage.getItem(PROMOS_KEY);
        if (pRaw && pRaw !== lastPromos) {
          lastPromos = pRaw;
          try { setPromos(JSON.parse(pRaw)); } catch { /* ignored */ }
        }
      }, 700);
    }

    return () => {
      if (bc) bc.close();
      if (pollId) clearInterval(pollId);
    };
  }, []);

  const currency = currencyOf(branding);
  const sym = currency || '';
  const logo = branding?.logo_url || branding?.thermal_logo_url;
  const activePromos = useMemo(() => (Array.isArray(promos) ? promos.filter((p) => p && p.active && p.image_url) : []), [promos]);

  // ── Empty / idle state ────────────────────────────────────────────
  if (state.status !== 'paid' && state.cart.length === 0) {
    if (activePromos.length > 0) {
      return <PromoRotator promos={activePromos} branding={branding} setShow={setShowHelp} show={showHelp} />;
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-zinc-900 text-white flex flex-col items-center justify-center px-8 text-center" data-testid="customer-display-idle">
        {logo && (
          <img src={resolveUploadUrl(logo)} alt="" className="w-32 h-32 rounded-3xl object-contain bg-white/10 p-3 mb-6 shadow-2xl" />
        )}
        <h1 className="text-5xl font-bold tracking-tight" data-testid="cd-company">{branding?.company_name || 'Welcome'}</h1>
        <p className="text-xl text-white/70 mt-3">{branding?.tagline || 'Thanks for visiting — we\u2019ll be with you shortly.'}</p>
        <HelpButton show={showHelp} setShow={setShowHelp} />
      </div>
    );
  }

  // ── Paid / thank-you state ────────────────────────────────────────
  if (state.status === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 text-white flex flex-col items-center justify-center px-8 text-center" data-testid="customer-display-paid">
        <div className="w-24 h-24 rounded-full bg-white/15 backdrop-blur flex items-center justify-center mb-6">
          <CheckCircle2 size={56} strokeWidth={1.8} />
        </div>
        <h1 className="text-6xl font-extrabold tracking-tight" data-testid="cd-thanks-title">Thank you!</h1>
        <p className="text-2xl text-white/85 mt-4 flex items-center gap-2">
          <Heart size={22} fill="currentColor" /> See you again soon.
        </p>
        {state.orderNo && <p className="text-sm text-white/60 mt-8" data-testid="cd-order-no">Order #{state.orderNo}</p>}
        {branding?.company_name && <p className="text-xs text-white/50 mt-1">{branding.company_name}</p>}
        <HelpButton show={showHelp} setShow={setShowHelp} />
      </div>
    );
  }

  // ── Active cart state ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="customer-display-cart">
      {/* Header */}
      <div className="bg-white shadow-sm px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {logo && <img src={resolveUploadUrl(logo)} alt="" className="w-12 h-12 rounded-xl object-contain bg-zinc-100 p-1" />}
          <div>
            <h1 className="font-bold text-2xl text-zinc-900" data-testid="cd-active-company">{branding?.company_name || 'Your order'}</h1>
            {state.branch && <p className="text-xs text-zinc-500" data-testid="cd-active-branch">{state.branch}</p>}
          </div>
        </div>
        <HelpButton show={showHelp} setShow={setShowHelp} inline />
      </div>

      {/* Cart items */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
          <ShoppingBag size={14} /> Your order · {state.cart.length} item{state.cart.length === 1 ? '' : 's'}
        </p>
        <div className="space-y-2">
          {state.cart.map((line, i) => (
            <div key={line.id || `${line.name}-${i}`} className="flex items-baseline justify-between bg-white rounded-xl px-5 py-3 shadow-sm" data-testid={`cd-line-${i}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-bold text-xl text-zinc-700 w-10 text-right">{line.qty || line.quantity || 1}×</span>
                <div>
                  <p className="font-semibold text-zinc-900 text-lg">{line.name}</p>
                  {line.modifiers?.length > 0 && (
                    <p className="text-xs text-zinc-500">{line.modifiers.map((m) => m.name || m).join(', ')}</p>
                  )}
                </div>
              </div>
              <p className="font-bold text-xl text-zinc-900 tabular-nums">{sym} {(line.subtotal || line.price * (line.qty || 1)).toFixed(2)}</p>
            </div>
          ))}
        </div>

        {/* iter703 — Upsell overlay card. Shows only while cart is
            active AND the cashier POS has pushed at least one
            suggestion through the BroadcastChannel. */}
        {upsells.length > 0 && (
          <UpsellOverlay upsells={upsells} sym={sym} />
        )}
      </div>

      {/* Totals */}
      <div className="bg-white border-t border-zinc-200 px-8 py-5">
        <div className="space-y-1.5 mb-3 text-zinc-600">
          <Row label="Subtotal" value={`${sym} ${(state.subtotal || 0).toFixed(2)}`} testId="cd-subtotal" />
          {state.discount > 0 && <Row label="Discount" value={`- ${sym} ${state.discount.toFixed(2)}`} testId="cd-discount" tone="rose" />}
          {state.tax > 0 && <Row label="VAT" value={`${sym} ${state.tax.toFixed(2)}`} testId="cd-tax" />}
        </div>
        <div className="border-t border-zinc-200 pt-3 flex items-baseline justify-between">
          <span className="font-bold text-2xl text-zinc-900">Total</span>
          <span className="font-extrabold text-3xl text-emerald-600 tabular-nums" data-testid="cd-total">{sym} {(state.total || 0).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testId, tone }) {
  const toneCls = tone === 'rose' ? 'text-rose-600' : '';
  return (
    <div className={`flex items-baseline justify-between ${toneCls}`} data-testid={testId}>
      <span className="text-sm">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

// ── Upsell overlay (active cart) ──────────────────────────────────────
// A friendly "Add fries · +5 SAR?" card under the cart items. Purely
// visual — the cashier still does the 1-tap add on the POS side. We
// auto-rotate through suggestions so a long list doesn't fight for
// vertical space on the customer monitor.
function UpsellOverlay({ upsells, sym }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (upsells.length <= 1) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % upsells.length), 5000);
    return () => clearTimeout(t);
  }, [idx, upsells.length]);
  const cur = upsells[idx % upsells.length];
  if (!cur) return null;
  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white p-5 shadow-xl flex items-center gap-4 animate-[slidein_0.4s_ease-out]" data-testid="customer-display-upsell">
      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
        <Sparkles size={28} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider opacity-80 font-semibold mb-0.5">Add to your order?</p>
        <p className="font-extrabold text-2xl truncate" data-testid="cd-upsell-name">{cur.name}</p>
        {typeof cur.price === 'number' && (
          <p className="text-base opacity-95" data-testid="cd-upsell-price">+ {sym} {cur.price.toFixed(2)}</p>
        )}
      </div>
      <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-1 text-white">
        <Plus size={28} />
        <span className="text-[10px] uppercase opacity-80">just say yes</span>
      </div>
    </div>
  );
}

// ── Promo rotator (idle screen) ───────────────────────────────────────
// Crossfades through `promos[]` using each slide's own `duration_seconds`.
// We intentionally render the next slide on top with `opacity-0 → 100`
// so the transition is a buttery fade rather than a jarring swap.
function PromoRotator({ promos, branding, show, setShow }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!promos.length) return undefined;
    const current = promos[idx % promos.length];
    const ms = Math.max(2, Number(current?.duration_seconds) || 6) * 1000;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % promos.length);
    }, ms);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [idx, promos]);

  const slide = promos[idx % promos.length];
  if (!slide) return null;

  return (
    <div className="min-h-screen relative overflow-hidden bg-black" data-testid="customer-display-promo">
      {/* Each slide as an absolutely-positioned layer; the active one
          fades in, all others fade out. Keeps GPU-accelerated. */}
      {promos.map((p, i) => (
        <div
          key={p.id || `${p.image_url}-${i}`}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={i !== idx}
        >
          <img
            src={resolveUploadUrl(p.image_url)}
            alt={p.caption || ''}
            className="w-full h-full object-cover"
            data-testid={`cd-promo-image-${i}`}
          />
          {/* Dim overlay so any caption stays readable on busy art. */}
          {(p.caption || p.subtitle) && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          )}
          {(p.caption || p.subtitle) && (
            <div className="absolute bottom-10 left-10 right-10 text-white">
              {p.caption && <h2 className="text-5xl sm:text-6xl font-extrabold tracking-tight drop-shadow-2xl" data-testid={`cd-promo-caption-${i}`}>{p.caption}</h2>}
              {p.subtitle && <p className="text-xl sm:text-2xl text-white/90 mt-3 drop-shadow-md max-w-3xl" data-testid={`cd-promo-subtitle-${i}`}>{p.subtitle}</p>}
            </div>
          )}
        </div>
      ))}

      {/* Subtle progress dots so the cashier knows the rotator is alive. */}
      <div className="absolute top-6 right-6 flex gap-1.5">
        {promos.map((_, i) => (
          <span
            key={i}
            className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white w-6' : 'bg-white/50'}`}
            data-testid={`cd-promo-dot-${i}`}
          />
        ))}
      </div>

      {/* Brand badge — keeps the screen on-brand even between captions. */}
      {branding?.company_name && (
        <div className="absolute top-6 left-6 bg-black/40 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-2">
          {branding?.logo_url && <img src={resolveUploadUrl(branding.logo_url)} alt="" className="w-4 h-4 rounded-full" />}
          {branding.company_name}
        </div>
      )}

      <HelpButton show={show} setShow={setShow} />
    </div>
  );
}

/** Floating Help button that explains how to wire this window. */
function HelpButton({ show, setShow, inline }) {
  return (
    <>
      <button
        type="button"
        onClick={() => setShow(true)}
        className={inline
          ? 'flex items-center gap-1.5 text-xs text-zinc-500 hover:text-primary'
          : 'fixed bottom-6 right-6 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white/80 hover:bg-white/25 text-xs'}
        data-testid="cd-help-btn"
      >
        <Info size={13} /> How to connect
      </button>
      {show && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <div className="bg-white rounded-2xl max-w-md p-6 space-y-3 shadow-2xl text-zinc-800" onClick={(e) => e.stopPropagation()} data-testid="cd-help-dialog">
            <h2 className="font-bold text-lg flex items-center gap-2"><Info size={18} className="text-primary" /> Customer Display setup</h2>
            <ol className="list-decimal ml-5 text-sm space-y-2 leading-relaxed">
              <li>On the cashier tablet, open <strong>Cashier POS → ⋯ More → Customer Display</strong>.</li>
              <li>The page that pops open is <em>this</em> page. Drag the window onto your second monitor and tap full-screen (F11) so the buyer sees nothing but their order.</li>
              <li>As soon as the cashier adds items, the cart mirrors here in real time — no refresh needed.</li>
              <li>While the cart is empty, slides from <strong>Settings → Customer Display Promos</strong> rotate full-screen.</li>
              <li>After payment is taken the screen flips to a green <strong>Thank you</strong> for ~10 seconds, then resets for the next customer.</li>
            </ol>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Uses <code>BroadcastChannel</code> (same-origin, in-browser) so there&apos;s no LAN setup and no backend round-trip. If the customer display
              isn&apos;t a touch device, you can also keep this window minimised — Smarto will reactivate it the moment the cashier starts a new ticket.
            </p>
            <div className="pt-2 flex justify-end">
              <button onClick={() => setShow(false)} className="px-3 py-1.5 rounded-lg bg-primary text-white text-sm" data-testid="cd-help-close">Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
